import { api } from './api';
import type { AxiosRequestConfig } from 'axios';
import type { InstitutionPlayerBrandingPayload } from './institution-player-branding';

// ── Types ────────────────────────────────────────────────────────────────────

/** Voix renvoyée par GET /podcasts/voices (Mistral TTS). */
export interface PodcastVoice {
  voice_id: string;
  name: string;
  category: string;
  labels: Record<string, string>;
  preview_url: string;
  description?: string;
  image_url?: string;
}

export interface ListVoicesResponse {
  voices: PodcastVoice[];
  /** Clé Mistral résolue (intégrations plateforme / env). */
  mistralConfigured: boolean;
}

/**
 * Tolère l’ancien format (tableau seul), une enveloppe `{ data: … }`, ou des champs manquants.
 */
export function normalizeListVoicesResponse(raw: unknown): ListVoicesResponse {
  if (Array.isArray(raw)) {
    const voices = raw as PodcastVoice[];
    const mistralConfigured = voices.length > 0;
    return { voices, mistralConfigured };
  }
  if (raw && typeof raw === 'object') {
    const root = raw as Record<string, unknown>;
    if (Array.isArray(root.data)) {
      const voices = root.data as PodcastVoice[];
      const mistralFlag = root.mistralConfigured;
      const legacyFlag = root.elevenlabsConfigured;
      let mistralConfigured = voices.length > 0;
      if (typeof mistralFlag === 'boolean') mistralConfigured = mistralFlag;
      else if (typeof legacyFlag === 'boolean') mistralConfigured = legacyFlag;
      return { voices, mistralConfigured };
    }
    const inner =
      root.data !== undefined &&
      root.data !== null &&
      typeof root.data === 'object' &&
      !Array.isArray(root.data)
        ? (root.data as Record<string, unknown>)
        : root;
    const vr = inner.voices;
    const voices = Array.isArray(vr) ? (vr as PodcastVoice[]) : [];
    const mistralFlag = inner.mistralConfigured ?? root.mistralConfigured;
    const legacyFlag = inner.elevenlabsConfigured ?? root.elevenlabsConfigured;
    let mistralConfigured = false;
    if (typeof mistralFlag === 'boolean') {
      mistralConfigured = mistralFlag;
    } else if (typeof legacyFlag === 'boolean') {
      mistralConfigured = legacyFlag;
    } else if (voices.length > 0) {
      mistralConfigured = true;
    }
    return { voices, mistralConfigured };
  }
  return { voices: [], mistralConfigured: false };
}

export interface Podcast {
  id: string;
  title: string;
  description: string;
  coverImageUrl: string | null;
  categoryId: string | null;
  categoryName: string | null;
  language: string;
  /** Ordre d’affichage des épisodes liés au podcast. */
  episodeSortOrder?: 'newest_first' | 'oldest_first';
  isActive: boolean;
  /** Chaîne archivée : masquée des vues « Tous » et des parcours apprenants. */
  isArchived?: boolean;
  episodeCount: number;
  subscriberCount: number;
  createdBy: string | null;
  createdAt: string;
  updatedAt: string;
  /** Podcast pré-créé par la RH (besoin entreprise) */
  fromInstitutionTrainingNeed?: boolean;
  institutionId?: string | null;
  /** Présent si le créateur du podcast est rattaché à une institution ayant personnalisé son thème */
  institutionBranding?: InstitutionPlayerBrandingPayload | null;
  excerptEpisodeId?: string | null;
  excerptBlockId?: string | null;
  creationMode?: 'simple' | 'advanced';
}

export type PodcastExcerptClip = {
  episodeId: string;
  blockId: string;
  episodeTitle: string;
  blockText: string;
  audioUrl: string;
  startSeconds: number;
  endSeconds: number;
};

export interface Episode {
  id: string;
  title: string;
  description: string;
  status: 'draft' | 'published';
  voiceName: string | null;
  voiceId: string | null;
  audioDurationSeconds: number | null;
  audioFilePath: string | null;
  /** Renseigné par l’API si le MP3 existe sur le serveur (`/uploads/tts/…`). */
  audioUrl?: string | null;
  coverImageUrl: string | null;
  blocks: Array<{
    id?: string;
    text: string;
    voiceId: string;
    chapterId: string;
    audioSource?: 'tts' | 'recording';
    recordingPath?: string;
    pauseBeforeSeconds?: number;
    playbackStartSeconds?: number;
    playbackEndSeconds?: number;
  }>;
  chapters: Array<{ id: string; title: string; readTitleByAi?: boolean; readByAi?: boolean }>;
  musicTracks: Array<Record<string, unknown>>;
  podcastId: string | null;
  podcastIds: string[];
  /** Langue du 1er podcast lié (repli voix Marie/Paul). */
  podcastLanguage?: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface PaginatedResponse<T> {
  data: T[];
  total: number;
  page: number;
  totalPages: number;
}

export interface GenerateAudioResult {
  filePath: string;
  fileName: string;
  durationSeconds: number;
  audioUrl: string;
  blockPlaybackTimings?: Record<string, { start: number; end: number }>;
}

export interface PodcastPdfImportPreview {
  documentTitle: string;
  pageCount: number;
  sourceFormat?: 'pdf' | 'markdown';
  lineCount?: number;
  generatedMarkdown?: string;
  needsMarkdownConfirmation?: boolean;
  episodes: Array<{
    title: string;
    chapters: Array<{ title: string; sections: string[] }>;
  }>;
  warnings: string[];
  stats: { episodeCount: number; chapterCount: number; blockCount: number };
  diagnostics?: {
    stats: { episodeCount: number; chapterCount: number; blockCount: number };
    h1Count: number;
    h2Count: number;
    lineCount: number;
    charCount: number;
    issues: Array<{
      severity: 'error' | 'warning' | 'info';
      code: string;
      message: string;
      line?: number;
    }>;
    suggestions: string[];
  };
}

export interface PodcastPdfImportResult {
  createdEpisodeIds: string[];
  warnings: string[];
  stats: { episodeCount: number; chapterCount: number; blockCount: number };
}

function unwrapApiData<T>(raw: unknown): T {
  if (raw && typeof raw === 'object' && 'data' in raw && (raw as { data: unknown }).data !== undefined) {
    return (raw as { data: T }).data;
  }
  return raw as T;
}

/** Réponse POST `/tts/preview` (aperçu court, ex. voix Mistral sans `preview_url`). */
export interface TtsVoicePreviewResult {
  audioUrl: string;
  duration: number;
  provider: string;
  format: string;
  size: number;
}

function unwrapTtsPreviewResponse(raw: unknown): TtsVoicePreviewResult {
  if (!raw || typeof raw !== 'object') throw new Error('Réponse TTS preview invalide');
  const o = raw as Record<string, unknown>;
  if (o.success === true && o.data !== null && typeof o.data === 'object') {
    const d = o.data as Record<string, unknown>;
    if (typeof d.audioUrl === 'string') return o.data as TtsVoicePreviewResult;
  }
  if (typeof o.audioUrl === 'string') return raw as TtsVoicePreviewResult;
  throw new Error('Réponse TTS preview invalide');
}

// ── API Client ───────────────────────────────────────────────────────────────

export const podcastApi = {
  // Voices
  listVoices: async (): Promise<ListVoicesResponse> => {
    const raw = await api.get<unknown>('/podcasts/voices', undefined, {
      timeout: 120_000,
    } satisfies AxiosRequestConfig);
    return normalizeListVoicesResponse(raw);
  },

  /** Aperçu audio court (Mistral TTS). */
  previewTtsVoice: async (voiceId: string): Promise<TtsVoicePreviewResult> => {
    const raw = await api.post<unknown>('/tts/preview', { voiceId });
    return unwrapTtsPreviewResponse(raw);
  },

  // Podcasts
  listPodcasts: (params?: Record<string, unknown>) =>
    api.get<PaginatedResponse<Podcast>>('/podcasts', params),
  createPodcast: (data: Partial<Podcast>) =>
    api.post<Podcast>('/podcasts', data),
  getPodcast: (id: string) =>
    api.get<Podcast>(`/podcasts/${id}`),
  updatePodcast: (id: string, data: Partial<Podcast>) =>
    api.put<Podcast>(`/podcasts/${id}`, data),
  deletePodcast: (id: string) =>
    api.delete(`/podcasts/${id}`),
  restorePodcast: (id: string) =>
    api.post<{ message?: string }>(`/podcasts/${id}/restore`, {}),

  // Episodes
  listEpisodes: (params?: Record<string, unknown>) =>
    api.get<PaginatedResponse<Episode>>('/podcasts/episodes', params),
  getEpisode: (id: string) =>
    api.get<Episode>(`/podcasts/episodes/${id}`),
  createEpisode: (data: Partial<Episode> & { podcastId?: string }) =>
    api.post<Episode>('/podcasts/episodes', data),
  updateEpisode: (id: string, data: Partial<Episode>) =>
    api.put<Episode>(`/podcasts/episodes/${id}`, data),
  patchEpisode: (id: string, data: Partial<Episode>) =>
    api.patch<Episode>(`/podcasts/episodes/${id}`, data),
  deleteEpisode: (id: string) =>
    api.delete(`/podcasts/episodes/${id}`),

  // Episode ↔ Podcast assignment (many-to-many)
  assignEpisode: (episodeId: string, podcastId: string) =>
    api.post<Episode>(`/podcasts/episodes/${episodeId}/assign`, { podcastId }),
  unassignEpisode: (episodeId: string, podcastId: string) =>
    api.post<Episode>(`/podcasts/episodes/${episodeId}/unassign`, { podcastId }),

  // Audio generation
  generateAudio: async (
    episodeId: string,
    data: {
      voiceId: string;
      chapters?: Array<{ id: string; title: string; readTitleByAi?: boolean; readByAi?: boolean }>;
      blocks: Array<{
        id?: string;
        text: string;
        voiceId: string;
        chapterId?: string;
        audioSource?: 'tts' | 'recording';
        recordingPath?: string;
        pauseBeforeSeconds?: number;
      }>;
      musicTracks?: Array<Record<string, unknown>>;
    },
  ): Promise<GenerateAudioResult> => {
    const raw = await api.post<unknown>(
      `/podcasts/episodes/${episodeId}/generate-audio`,
      data,
      { timeout: 600_000 } satisfies AxiosRequestConfig,
    );
    return unwrapApiData<GenerateAudioResult>(raw);
  },

  // Cover upload
  uploadCover: (episodeId: string, file: File) =>
    api.upload<{ coverImageUrl: string }>(`/podcasts/episodes/${episodeId}/upload-cover`, file),

  uploadPodcastCover: (podcastId: string, file: File) =>
    api.upload<{ coverImageUrl: string }>(`/podcasts/${podcastId}/upload-cover`, file),

  previewPdfImport: async (podcastId: string, file: File): Promise<PodcastPdfImportPreview> => {
    const raw = await api.upload<unknown>(`/podcasts/${podcastId}/import-pdf/preview`, file);
    return unwrapApiData<PodcastPdfImportPreview>(raw);
  },

  previewMarkdownImport: async (
    podcastId: string,
    markdown: string,
    documentTitle?: string,
  ): Promise<PodcastPdfImportPreview> => {
    const raw = await api.post<unknown>(`/podcasts/${podcastId}/import-pdf/preview-markdown`, {
      markdown,
      documentTitle,
    });
    return unwrapApiData<PodcastPdfImportPreview>(raw);
  },

  confirmPdfImport: async (
    podcastId: string,
    file: File,
    voiceId?: string,
  ): Promise<PodcastPdfImportResult> => {
    const formData = new FormData();
    formData.append('file', file);
    if (voiceId?.trim()) formData.append('voiceId', voiceId.trim());
    const raw = await api.upload<unknown>(`/podcasts/${podcastId}/import-pdf`, formData);
    return unwrapApiData<PodcastPdfImportResult>(raw);
  },

  confirmMarkdownImport: async (
    podcastId: string,
    markdown: string,
    options?: { voiceId?: string; documentTitle?: string },
  ): Promise<PodcastPdfImportResult> => {
    const formData = new FormData();
    formData.append('markdown', markdown);
    if (options?.documentTitle?.trim()) {
      formData.append('documentTitle', options.documentTitle.trim());
    }
    if (options?.voiceId?.trim()) formData.append('voiceId', options.voiceId.trim());
    const raw = await api.upload<unknown>(`/podcasts/${podcastId}/import-pdf`, formData);
    return unwrapApiData<PodcastPdfImportResult>(raw);
  },

  /** Enregistrement micro pour un bloc (WebM/WAV/etc. → MP3 côté serveur). */
  uploadBlockRecording: async (
    episodeId: string,
    blockId: string,
    blob: Blob,
    filename = 'recording.webm',
  ): Promise<{ recordingPath: string }> => {
    const formData = new FormData();
    formData.append('file', blob, filename);
    formData.append('blockId', blockId);
    return api.upload<{ recordingPath: string }>(
      `/podcasts/episodes/${episodeId}/upload-block-audio`,
      formData,
    );
  },
};
